'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import Link from 'next/link';
import { aiModelsApi, Sam2Response } from '@/lib/ai-models-api';

// Distinct colors for mask overlays (RGB)
const MASK_COLORS = [
  [255, 59, 48],    // Red
  [52, 199, 89],    // Green
  [0, 122, 255],    // Blue
  [255, 204, 0],    // Yellow
  [175, 82, 222],   // Purple
  [255, 149, 0],    // Orange
  [90, 200, 250],   // Cyan
  [255, 45, 85],    // Pink
  [50, 215, 75],    // Mint
  [88, 86, 214],    // Indigo
];

type Sam2Mode = 'point' | 'box' | 'auto';

interface Point {
  x: number;
  y: number;
  label: number; // 1 = foreground, 0 = background
}

export default function Sam2Page() {
  const [image, setImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [mode, setMode] = useState<Sam2Mode>('auto');
  const [points, setPoints] = useState<Point[]>([]);
  const [box, setBox] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [isDrawingBox, setIsDrawingBox] = useState(false);
  const [boxStart, setBoxStart] = useState<{ x: number; y: number } | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Sam2Response | null>(null);
  const [combinedOverlay, setCombinedOverlay] = useState<string | null>(null);

  const imageRef = useRef<HTMLImageElement>(null);

  // Generate combined overlay image when results change
  useEffect(() => {
    if (!result || !imagePreview || result.masks.length === 0) {
      setCombinedOverlay(null);
      return;
    }

    const generateOverlay = async () => {
      try {
        // Load original image
        const originalImg = new Image();
        originalImg.crossOrigin = 'anonymous';

        await new Promise<void>((resolve, reject) => {
          originalImg.onload = () => resolve();
          originalImg.onerror = reject;
          originalImg.src = imagePreview;
        });

        // Create main canvas
        const canvas = document.createElement('canvas');
        canvas.width = originalImg.width;
        canvas.height = originalImg.height;
        const ctx = canvas.getContext('2d')!;

        // Draw original image
        ctx.drawImage(originalImg, 0, 0);

        // Process each mask
        for (let i = 0; i < result.masks.length; i++) {
          const maskData = result.masks[i].mask;
          const maskSrc = maskData.startsWith('data:') ? maskData : `data:image/png;base64,${maskData}`;
          const color = MASK_COLORS[i % MASK_COLORS.length];

          // Load mask image
          const maskImg = new Image();
          maskImg.crossOrigin = 'anonymous';

          await new Promise<void>((resolve, reject) => {
            maskImg.onload = () => resolve();
            maskImg.onerror = reject;
            maskImg.src = maskSrc;
          });

          // Create temporary canvas for mask processing
          const maskCanvas = document.createElement('canvas');
          maskCanvas.width = originalImg.width;
          maskCanvas.height = originalImg.height;
          const maskCtx = maskCanvas.getContext('2d')!;

          // Draw mask scaled to original image size
          maskCtx.drawImage(maskImg, 0, 0, originalImg.width, originalImg.height);

          // Get mask pixel data
          const maskImageData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
          const maskPixels = maskImageData.data;

          // Create colored overlay
          const overlayCanvas = document.createElement('canvas');
          overlayCanvas.width = originalImg.width;
          overlayCanvas.height = originalImg.height;
          const overlayCtx = overlayCanvas.getContext('2d')!;
          const overlayImageData = overlayCtx.createImageData(overlayCanvas.width, overlayCanvas.height);
          const overlayPixels = overlayImageData.data;

          // Apply color where mask is white (or non-black)
          for (let j = 0; j < maskPixels.length; j += 4) {
            const brightness = (maskPixels[j] + maskPixels[j + 1] + maskPixels[j + 2]) / 3;
            if (brightness > 128) {
              overlayPixels[j] = color[0];       // R
              overlayPixels[j + 1] = color[1];   // G
              overlayPixels[j + 2] = color[2];   // B
              overlayPixels[j + 3] = 115;        // A (45% of 255 = 115)
            }
          }

          overlayCtx.putImageData(overlayImageData, 0, 0);

          // Composite overlay onto main canvas
          ctx.drawImage(overlayCanvas, 0, 0);
        }

        // Convert to data URL
        setCombinedOverlay(canvas.toDataURL('image/png'));
      } catch (err) {
        console.error('Failed to generate combined overlay:', err);
        setCombinedOverlay(null);
      }
    };

    generateOverlay();
  }, [result, imagePreview]);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    // Get image dimensions
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
    };
    img.src = URL.createObjectURL(file);

    setImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
    setPoints([]);
    setBox(null);
  }, []);

  const handleImageClick = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    if (!imageRef.current || !imageDimensions) return;

    const rect = imageRef.current.getBoundingClientRect();
    const scaleX = imageDimensions.width / rect.width;
    const scaleY = imageDimensions.height / rect.height;

    const x = Math.round((e.clientX - rect.left) * scaleX);
    const y = Math.round((e.clientY - rect.top) * scaleY);

    if (mode === 'point') {
      const isRightClick = e.button === 2;
      setPoints((prev) => [...prev, { x, y, label: isRightClick ? 0 : 1 }]);
    } else if (mode === 'box') {
      if (!isDrawingBox) {
        setBoxStart({ x, y });
        setIsDrawingBox(true);
      } else {
        setBox({
          x1: Math.min(boxStart!.x, x),
          y1: Math.min(boxStart!.y, y),
          x2: Math.max(boxStart!.x, x),
          y2: Math.max(boxStart!.y, y),
        });
        setIsDrawingBox(false);
        setBoxStart(null);
      }
    }
  }, [mode, isDrawingBox, boxStart, imageDimensions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) return;

    setLoading(true);
    setError(null);

    try {
      let response: Sam2Response;

      switch (mode) {
        case 'point':
          if (points.length === 0) {
            throw new Error('Please add at least one point');
          }
          response = await aiModelsApi.sam2SegmentPoint(image, points);
          break;
        case 'box':
          if (!box) {
            throw new Error('Please draw a bounding box');
          }
          response = await aiModelsApi.sam2SegmentBox(image, box);
          break;
        case 'auto':
          response = await aiModelsApi.sam2SegmentAuto(image);
          break;
        default:
          throw new Error('Unknown mode');
      }

      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">SAM2 - Segment Anything</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Parameters (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          ref={imageRef}
                          src={imagePreview}
                          alt="Input"
                          className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg cursor-crosshair"
                          onClick={handleImageClick}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            handleImageClick(e);
                          }}
                        />
                        {/* Render points */}
                        {points.map((point, idx) => {
                          if (!imageRef.current || !imageDimensions) return null;
                          const rect = imageRef.current.getBoundingClientRect();
                          const displayX = (point.x / imageDimensions.width) * rect.width;
                          const displayY = (point.y / imageDimensions.height) * rect.height;
                          return (
                            <div
                              key={idx}
                              className={`absolute w-3 h-3 rounded-full border-2 border-white transform -translate-x-1/2 -translate-y-1/2 ${
                                point.label === 1 ? 'bg-green-500' : 'bg-red-500'
                              }`}
                              style={{
                                left: displayX,
                                top: displayY,
                              }}
                            />
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                            setResult(null);
                            setPoints([]);
                            setBox(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Mode */}
                <div>
                  <label className="block text-sm font-medium mb-2">Segmentation Mode</label>
                  <select
                    value={mode}
                    onChange={(e) => {
                      setMode(e.target.value as Sam2Mode);
                      setPoints([]);
                      setBox(null);
                    }}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="auto">Automatic (Segment Everything)</option>
                    <option value="point">Point Prompts (Click on image)</option>
                    <option value="box">Bounding Box</option>
                  </select>
                </div>

                {/* Mode-specific instructions */}
                {mode === 'point' && image && (
                  <div className="p-3 bg-[var(--bg-primary)] rounded-lg text-sm text-[var(--text-muted)]">
                    <p>Left-click to add foreground points (green)</p>
                    <p>Right-click to add background points (red)</p>
                    {points.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setPoints([])}
                        className="mt-2 text-[var(--accent)] hover:underline"
                      >
                        Clear {points.length} points
                      </button>
                    )}
                  </div>
                )}

                {mode === 'box' && image && (
                  <div className="p-3 bg-[var(--bg-primary)] rounded-lg text-sm text-[var(--text-muted)]">
                    <p>{isDrawingBox ? 'Click to set second corner' : 'Click to start drawing box'}</p>
                    {box && (
                      <button
                        type="button"
                        onClick={() => setBox(null)}
                        className="mt-2 text-[var(--accent)] hover:underline"
                      >
                        Clear box
                      </button>
                    )}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!image || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Segmenting...' : 'Segment Image'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Results</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image and run segmentation</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Segmenting...</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Combined Overlay - Original with colored masks */}
                    {combinedOverlay && (
                      <div>
                        <p className="text-sm text-[var(--text-muted)] mb-2">Combined Overlay (Original + Masks)</p>
                        <img
                          src={combinedOverlay}
                          alt="Combined overlay"
                          className="w-full rounded-lg"
                        />
                        {/* Color Legend */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {result.masks.slice(0, 10).map((mask, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 text-xs">
                              <div
                                className="w-3 h-3 rounded"
                                style={{ backgroundColor: `rgb(${MASK_COLORS[idx % MASK_COLORS.length].join(',')})` }}
                              />
                              <span className="text-[var(--text-muted)]">
                                {mask.score ? `${(mask.score * 100).toFixed(0)}%` : `#${idx + 1}`}
                              </span>
                            </div>
                          ))}
                          {result.masks.length > 10 && (
                            <span className="text-xs text-[var(--text-muted)]">+{result.masks.length - 10} more</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* API Composite Image */}
                    {result.composite_image && (
                      <div>
                        <p className="text-sm text-[var(--text-muted)] mb-2">API Segmented Output</p>
                        <img
                          src={result.composite_image.startsWith('data:') ? result.composite_image : `data:image/png;base64,${result.composite_image}`}
                          alt="Segmented"
                          className="w-full rounded-lg"
                        />
                      </div>
                    )}

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Masks Found</p>
                        <p className="text-lg font-semibold">{result.masks.length}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Inference</p>
                        <p className="text-lg font-semibold">{result.inference_time_ms.toFixed(0)}ms</p>
                      </div>
                    </div>

                    {/* Masks List */}
                    {result.masks.length > 0 && (
                      <div>
                        <p className="text-sm text-[var(--text-muted)] mb-2">Individual Masks</p>
                        <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                          {result.masks.map((mask, idx) => (
                            <div key={idx} className="relative">
                              <img
                                src={mask.mask.startsWith('data:') ? mask.mask : `data:image/png;base64,${mask.mask}`}
                                alt={`Mask ${idx + 1}`}
                                className="w-full aspect-square object-cover rounded-lg bg-[var(--bg-primary)]"
                              />
                              <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 rounded text-xs">
                                {mask.score ? `${(mask.score * 100).toFixed(0)}%` : `#${idx + 1}`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
